/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import debounce from 'lodash.debounce';
import {getScheduledReports} from '@ion/core/src/services/Reports';
import {SisenseState} from '@ion/core/src/services/SisenseUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {AgGridType} from '@ion/dls/src/grid/IonGrid';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import useLoadGridData from '@ion/dls/src/grid/useLoadGridData';
import QueryKey from '@ion/query/src/config/QueryKeys';
import usePaginatedQueryCache from '@ion/query/src/hooks/usePaginatedQueryCache';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import useSisenseSelector from '../../sisense/hooks/useSisenseSelector';
import {buildSearchParams, SearchType} from './SearchTypes';
import {scheduledReportGridFilterModel} from './store';

/**
 * Interface representing the results returned by the `useLoadScheduledReports` hook.
 *
 * @property {Function} getScheduledReports - Method to load crashboxx events based on the provided parameters.
 * @property {FilterModel} filterModel - The filter model applied to the crashboxx events grid. This should be
 *                                        updated with the actual type of the filter model if known.
 * @property {boolean} refresh - A boolean indicating whether a refresh is currently in progress.
 * @property {UsePaginationResult} paging - Object containing pagination information and methods.
 * @property {boolean} fetching - A boolean indicating whether data fetching is currently in progress.
 * @property {Function} onRefresh - Function to manually trigger a refresh of the data.
 * @property {GenericObject} apiParams - Object containing the API parameters used to fetch crashboxx events.
 */
export interface UseLoadScheduledReportsResults {
  getAllScheduledReports: (params) => void;
  filterModel: FilterModel;
  refresh: boolean;
  paging: UsePaginationResult;
  fetching: boolean;
  onRefresh: Function;
  apiParams: GenericObject;
}

interface UseLoadScheduledReportsProps {
  gridApi: AgGridType;
  globalAccountId: number;
  viewSubGroups: boolean;
  searchValue: string;
  searchType?: SearchType;
}

/**
 * The React hook for loading Crashboxx events into an AgGrid instance with pagination support.
 * It handles fetching Crashboxx event data based on filter criteria and provides functions for refreshing data.
 * 
 * ```typescript
 * 
 *  import {useLoadScheduledReports} from './useLoadScheduledReports';
 * 
 *  const options = useLoadScheduledReports(gridApi, 9001, false);
 * 
 * ```
 * 
 * @param {AgGridType} gridApi - The AgGrid instance representing the grid.
 * @param {number} globalAccountId - The global account ID to be used for fetching Crashboxx events.
 * @param {boolean} viewSubGroups - Flag to indicate whether to include sub-groups in the data fetching.
 * @param {string} searchValue - The committed search term.
 * @param {SearchType} searchType - Which API query param the search term is sent as: `Any` uses the
 *                                  broad `search`/`filterOn` param, `RecipientEmail` uses the
 *                                  `recipientEmail` keyword list. See `buildSearchParams`.
 *
 * @returns {UseLoadScheduledReportsResults} An object containing methods and values for loading and managing
 *                                          Crashboxx event data.
 */
export const useLoadScheduledReports = ({
  gridApi,
  globalAccountId,
  viewSubGroups,
  searchValue,
  searchType = SearchType.Any
}: UseLoadScheduledReportsProps): UseLoadScheduledReportsResults => {
  const sisenseOptions      = useSisenseSelector(SisenseState.ServerOptions);
  const sisenseOptionsError = useSisenseSelector(SisenseState.SisenseOptionsError);
  const sessionCreated      = useSisenseSelector(SisenseState.SessionCreated);
  const isEnabled           = !!sessionCreated && !sisenseOptionsError && !!sisenseOptions;
  const filterModel = scheduledReportGridFilterModel.value;

  // Only one search param is ever sent: the API ignores every other filter when
  // the broad `search` param is present, so `search` and `recipientEmail` cannot
  // be combined.
  const searchParams = useMemo(
    () => buildSearchParams(searchValue, searchType),
    [searchValue, searchType]
  );

  const apiParams = useMemo(() => ({
    accountId        : globalAccountId,
    includeSubGroups : viewSubGroups,
    ...searchParams
  }), [globalAccountId, viewSubGroups, searchParams]);

  const {
    loadItems,
    refresh,
    paging,
    fetching,
    onRefresh
  } = usePaginatedQueryCache({
    api             : getScheduledReports,
    baseKey         : QueryKey.SCHEDULED_REPORTS,
    initialPageSize : 20,
    parseKey        : 'scheduledReports',
    apiParams,
    enabled         : isEnabled,
    enabledPrefetch : isEnabled
  });

  const getReports = useCallback(debounce(({page, ...params}) => {
    return loadItems({
      ...apiParams,
      ...params,
      searchTerms : {
        ...apiParams.searchTerms,
        ...params?.filter
      },
      pageId : page + 1
    });
  }, 350), [loadItems, apiParams]);

  const getAllScheduledReports = useCallback(useLoadGridData({
    loadData : getReports,
    filterModel,
    gridApi
  }), [getReports, filterModel, gridApi]);

  return {
    getAllScheduledReports,
    filterModel,
    refresh,
    paging,
    fetching,
    onRefresh,
    apiParams
  };
};

export default useLoadScheduledReports;
